import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&svg]:pointer-events-none [&svg:not([class*='size-'])]:size-4 shrink-0 [&svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        primary:
          "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90",
        white: "bg-white text-primary shadow-xs hover:bg-white",
        default:
          "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90",
        danger: "bg-red-700 text-primary-foreground shadow-xs hover:bg-red-700",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border border-primary text-primary bg-background shadow-xs hover:bg-primary hover:text-white dark:bg-input/30 dark:border-primary dark:hover:bg-primary/90 dark:hover:text-white",
        secondary:
          "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
        text: "",
        cancel:
          "border bg-background shadow-xs text-primary border-primary hover:bg-red-700 hover:text-white hover:border-red-700 dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        green: "bg-green-700 text-white shadow-xs hover:bg-green-600",
        blue: "bg-blue-700 text-white shadow-xs hover:bg-blue-600",
        iconButton:
          "border border-stone-200 bg-background dark:bg-input/30 dark:border-stone-950 dark:hover:bg-stone-950 dark:hover:text-stone-950 text-xs textstone-950 font-normal",
        dropdownButton:
          "border border-stone-200 bg-background dark:bg-input/30 dark:border-stone-950 dark:hover:bg-stone-950 dark:hover:text-stone-950 text-xs text-muted-foreground font-normal",
      },
      size: {
        default: "h-9 px-4 py-2.5 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "h-6 w-6 p-1.5 rounded border",
        smIcon: "h-8 w-8 p-1 rounded border",
        mdIcon: "h-10 w-10 p-2.5 rounded",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
